import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * @param {Element} slide - A slideshow slide.
 * @returns {boolean} Whether the slide contains a video.
 */
function isVideoSlide(slide) {
  return !!slide.querySelector('[data-media-type="video"], [data-media-type="external_video"]');
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('loadeddata', this.#handleVideoReady, { capture: true, signal });
    this.addEventListener('playing', this.#handleVideoReady, { capture: true, signal });
    if (this.hasAttribute('data-model-switch')) {
      this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
      this.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleMediaStarted, { signal });
      const initialMode = this.dataset.mediaMode;
      const mode = initialMode === '3d' || initialMode === 'video' ? initialMode : 'image';
      this.#setMediaMode(mode, mode !== 'image');
      window.ProductModel?.loadShopifyXR();
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#pauseMedia();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;
    if (this.dataset.productId && event.detail.data.productId && this.dataset.productId !== event.detail.data.productId) return;

    if (newMediaGallery.hasAttribute('data-model-switch')) {
      const slides = Array.from(newMediaGallery.querySelectorAll(':scope > slideshow-component slideshow-slide[slide-id]'));
      const modelIndex = slides.findIndex((slide) => slide.getAttribute('slide-id') === newMediaGallery.dataset.modelId);
      const videoIndex = slides.findIndex(isVideoSlide);
      const imageIndex = slides.findIndex((slide) => slide.querySelector('[data-media-type="image"]'));
      const sameProduct = this.dataset.productId === newMediaGallery.dataset.productId;
      const keepModel = this.dataset.mediaMode === '3d' && sameProduct && modelIndex >= 0;
      const keepVideo = this.dataset.mediaMode === 'video' && sameProduct && videoIndex >= 0;
      const mode = keepModel ? '3d' : keepVideo ? 'video' : 'image';
      newMediaGallery.dataset.mediaMode = mode;
      if (mode !== 'image') newMediaGallery.dataset.carouselActive = 'true';
      const slideshow = newMediaGallery.querySelector(':scope > slideshow-component');
      const index = keepModel ? modelIndex : keepVideo ? videoIndex : Math.max(0, imageIndex);
      if (slideshow instanceof HTMLElement) slideshow.dataset.initialSlide = String(index);
    }
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /** @param {'image' | 'video' | '3d'} mode */
  async selectMediaView(mode) {
    if (!this.hasAttribute('data-model-switch')) return;
    const slides = Array.from(this.querySelectorAll(':scope > slideshow-component slideshow-slide[slide-id]'));
    let slide;

    if (mode === '3d') {
      slide = slides.find((item) => item.getAttribute('slide-id') === this.dataset.modelId);
    } else if (mode === 'video') {
      slide = slides.find(isVideoSlide);
    } else {
      slide = slides.find((item) => item.querySelector('[data-media-type="image"]'));
    }

    if (!slide) return;
    if (mode !== 'image') this.dataset.carouselActive = 'true';
    await customElements.whenDefined('slideshow-component');
    if (!this.isConnected) return;
    await this.slideshow?.select({ id: slide.getAttribute('slide-id') }, undefined, { animate: false });
    this.#setMediaMode(mode, mode !== 'image');
  }

  /** @param {SlideshowSelectEvent} event */
  #handleSlideshowSelect = (event) => {
    if (event.target !== this.slideshow) return;
    const slide = event.detail.slide;
    const isModel = !!slide.querySelector('product-model');
    const mode = isModel ? '3d' : isVideoSlide(slide) ? 'video' : 'image';
    // A video slide that is only selected programmatically (e.g. the initial slide
    // on page load) must not force the gallery out of the grid view.
    const activateCarousel = mode === 'video' ? event.detail.userInitiated || this.dataset.carouselActive === 'true' : isModel;
    this.#setMediaMode(mode, activateCarousel);
    this.#pauseMedia(slide);
    if (isModel) {
      customElements.whenDefined('product-model').then(() => {
        if (!this.isConnected || this.dataset.mediaMode !== '3d') return;
        const model = slide.querySelector('product-model');
        if (!model?.hasAttribute('data-media-loaded')) model?.loadContent();
        else model.playMedia();
      });
    }
  };

  /**
   * @param {'image' | 'video' | '3d'} mode
   * @param {boolean} [activateCarousel] - Whether to switch the gallery to the carousel view.
   */
  #setMediaMode(mode, activateCarousel = mode === '3d') {
    this.dataset.mediaMode = mode;
    this.dataset.carouselActive = String(activateCarousel || this.dataset.presentation === 'carousel');
    this.querySelectorAll('[data-media-view]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-media-view') === mode));
    });
  }

  #handleMediaStarted = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('product-model')) return;
    if (!this.contains(event.target)) return;
    this.#setMediaMode('3d');
  };

  /**
   * Marks the media container as ready once the video's first frame can be painted,
   * so the video can fade in over the preview image instead of flashing.
   * @param {Event} event - The `loadeddata` or `playing` event of a video.
   */
  #handleVideoReady = (event) => {
    if (!(event.target instanceof HTMLVideoElement)) return;
    event.target.closest('.product-media-container')?.setAttribute('data-video-ready', '');
  };

  /** @param {Element} [activeSlide] */
  #pauseMedia(activeSlide) {
    this.querySelectorAll('product-model, deferred-media').forEach((media) => {
      if (!activeSlide?.contains(media)) media.pauseMedia?.();
    });
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.carouselActive === 'true' ? 'carousel' : this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
